'use client';

import { useTranslations } from 'next-intl';

import { MessageLocaleWrapper } from '@/components/assistant-ui/message-locale-wrapper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLocaleDictionary } from '@/hooks/use-locale-dictionary';
import { getNativeLanguageName, getTextDirection } from '@/lib/utils/locale-shared';

interface LanguageSwitchDialogProps {
  detectedLocale: string;
  currentLocale: string;
  onConfirm: () => void;
  onDismiss: () => void;
}

function preventDialogDismissal(event: Event) {
  event.preventDefault();
}

function SwitchOffer({ detectedName }: { detectedName: string }) {
  const t = useTranslations('languageDetection');

  return (
    <DialogHeader>
      <DialogTitle className="text-xl">{t('title')}</DialogTitle>
      <DialogDescription className="flex flex-col gap-1.5 text-sm">
        <span>{t('notice', { language: detectedName })}</span>
        <span>{t('question')}</span>
      </DialogDescription>
    </DialogHeader>
  );
}

function SwitchButton({
  offerLocale,
  offerDirection,
  detectedName,
  onConfirm,
}: {
  offerLocale: string;
  offerDirection: string;
  detectedName: string;
  onConfirm: () => void;
}) {
  const t = useTranslations('languageDetection');

  return (
    <Button variant="accent" dir={offerDirection} lang={offerLocale} onClick={onConfirm} autoFocus>
      {t('confirm', { language: detectedName })}
    </Button>
  );
}

export function LanguageSwitchDialog({
  detectedLocale,
  currentLocale,
  onConfirm,
  onDismiss,
}: LanguageSwitchDialogProps) {
  const t = useTranslations('languageDetection');
  const { dictionary, settled } = useLocaleDictionary(detectedLocale);

  const detectedName = getNativeLanguageName(detectedLocale);
  const currentName = getNativeLanguageName(currentLocale);

  if (!settled) return null;

  // Whichever language the offer is actually rendered in drives the layout.
  // Without the detected language's dictionary the wording falls back to the
  // page — switching still works, which beats withholding the offer from the
  // one visitor who cannot read the interface.
  const offerLocale = dictionary ? detectedLocale : currentLocale;
  const offerDirection = getTextDirection(offerLocale);

  return (
    <Dialog open>
      <DialogContent
        role="alertdialog"
        showCloseButton={false}
        onPointerDownOutside={preventDialogDismissal}
        onEscapeKeyDown={preventDialogDismissal}
      >
        <div className="flex flex-col gap-5">
          <MessageLocaleWrapper locale={offerLocale}>
            <SwitchOffer detectedName={detectedName} />
          </MessageLocaleWrapper>
          <DialogFooter dir={offerDirection}>
            <Button
              variant="outline"
              dir={getTextDirection(currentLocale)}
              lang={currentLocale}
              onClick={onDismiss}
            >
              {t('dismiss', { language: currentName })}
            </Button>
            <MessageLocaleWrapper locale={offerLocale} inline>
              <SwitchButton
                offerLocale={offerLocale}
                offerDirection={offerDirection}
                detectedName={detectedName}
                onConfirm={onConfirm}
              />
            </MessageLocaleWrapper>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
